import Sentry from "@/integrations/Sentry";
import { onAppResumed } from "@/native/appLifecycle";
import { queryClient as appQueryClient } from "@/queryClient";
import { isRestoredQueryData } from "@/queryPersistence";
import {
  FILE_RECORD_BATCH_GET_LIMIT,
  FILE_RECORD_BATCH_GET_TIMEOUT_MS,
  type FileRecord,
  type FileRecordBatchGetItem,
  type FileRecordBatchGetResult,
} from "@cohesive/api-client";
import {
  getPresignedUrlExpiresAtMs,
  presignedResourceKey,
} from "@cohesive/ui-components";
import {
  notifyManager,
  replaceEqualDeep,
  type Query,
  type QueryCache,
  type QueryClient,
} from "@tanstack/react-query";
import createDebug from "debug";
import { orgAPI } from "./api";
import { makeRoom } from "./boundedMap";
import { earliestPresignExpiryMs, fileRecordKeys } from "./fileRecords";
import { apiCaptureContext } from "./sentryFingerprint";
import { ApiError } from "./utils";

/**
 * Every server read of a `["fileRecord", id]` row, and the freshness of the
 * rows on screen.
 *
 * Loads go through {@link getFileRecord}: the calls made inside one macrotask
 * leave as one `batch get` request per 50 ids, at most {@link MAX_IN_FLIGHT} at a
 * time per tab. {@link startFileRecordRefresh} keeps the rows something renders
 * fresh over the same transport — one grouped call per tick per 50 rows,
 * asking with the fingerprint the server last answered with, and renewing
 * links before they expire.
 *
 * Every row the server answers with lands through {@link mergeFileRecordLinks}
 * so a cached link with life left in it is kept: a new signature for the same
 * bytes would only make the browser download them again.
 */

const debug = createDebug("cohesive:file-record-refresh");

const MAX_IN_FLIGHT = 4;

/** How often a visible tab asks what changed. */
export const FILE_RECORD_CHECK_INTERVAL_MS = 5 * 60_000;
const CHECK_JITTER_MS = 60_000;
/** A row answered for this recently is left alone by the next trigger. */
const VERIFIED_RECENTLY_MS = 60_000;
/**
 * A cached link is kept while it has more than this left and renewed once it
 * has less. Two ticks fit inside it, so no link dies waiting for one.
 */
const LINK_HORIZON_MS = 15 * 60_000;

interface RowState {
  /** What the server last answered with; undefined once a merge invalidates it. */
  fingerprint?: string;
  /** The row the fingerprint belongs to. */
  updatedAt: string;
  verifiedAtMs: number;
}

/** Bounded: a tab open for days must not remember every row it ever saw. */
const rowStates = new Map<string, RowState>();

interface Waiter {
  client: QueryClient;
  cachedFallback: boolean;
  resolve: (record: FileRecord) => void;
  reject: (error: unknown) => void;
}

export interface GetFileRecordOptions {
  /**
   * Whether an `unsure` answer may be settled with the row already held.
   * Callers that read meaning into a field the cached row may have lost —
   * the collaboration seed reads `url` — pass `false` and get the single GET.
   */
  cachedFallback?: boolean;
}

let pending: Map<string, Waiter[]> = new Map();
let windowScheduled = false;
let inFlight = 0;
const slotQueue: Array<() => void> = [];

export function getFileRecord(
  id: string,
  client: QueryClient = appQueryClient,
  options: GetFileRecordOptions = {},
): Promise<FileRecord> {
  return new Promise<FileRecord>((resolve, reject) => {
    const waiter = {
      client,
      cachedFallback: options.cachedFallback ?? true,
      resolve,
      reject,
    };
    const waiters = pending.get(id);
    if (waiters) {
      waiters.push(waiter);
    } else {
      pending.set(id, [waiter]);
    }
    if (!windowScheduled) {
      windowScheduled = true;
      setTimeout(flushWindow, 0);
    }
  });
}

function flushWindow() {
  windowScheduled = false;
  const batch = pending;
  pending = new Map();
  for (const chunk of chunked(Array.from(batch.keys()))) {
    void sendChunk(
      chunk,
      new Map(chunk.map((id) => [id, batch.get(id) ?? []])),
    );
  }
}

function chunked<T>(items: T[]): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += FILE_RECORD_BATCH_GET_LIMIT) {
    chunks.push(items.slice(i, i + FILE_RECORD_BATCH_GET_LIMIT));
  }
  return chunks;
}

async function sendChunk(ids: string[], waiters: Map<string, Waiter[]>) {
  let results: FileRecordBatchGetResult[];
  try {
    results = await withBatchGetSlot(() =>
      orgAPI.fileRecords.batchGet(ids.map((id) => ({ id }))),
    );
  } catch (error) {
    // The CALL failed — no route yet on the deployed backend, a 5xx, a 429,
    // no network. That says nothing about any one row, and handing each id
    // the call's status would have `isFileRecordGoneError` read a 404 as
    // "this file is gone" on every card at once. Transient, status 0, the
    // shape a network failure has always had.
    const transient = transportError(error);
    for (const list of waiters.values()) {
      for (const waiter of list) waiter.reject(transient);
    }
    return;
  }

  const byId = new Map(results.map((result) => [result.id, result]));
  for (const [id, list] of waiters) {
    const result = byId.get(id);
    captureBatchGetItemFailure(result);
    for (const waiter of list) {
      const cached = waiter.client.getQueryData<FileRecord>(
        fileRecordKeys.detail(id),
      );
      if (result?.status === 200 && result.file_record) {
        waiter.resolve(
          adopt(cached, id, result.file_record, result.fingerprint),
        );
      } else if (
        result?.unsure &&
        cached &&
        waiter.cachedFallback &&
        isLiveCachedRow(waiter.client, id, cached)
      ) {
        waiter.resolve(cached);
      } else if (result?.unsure) {
        // Nothing usable held for this row and the server could not answer in
        // full. The single GET still returns the degraded row it has always
        // returned, so ask it rather than failing the card.
        void fetchSingle(id).then(
          (row) => waiter.resolve(adopt(cached, id, row)),
          (error: unknown) => waiter.reject(error),
        );
      } else {
        waiter.reject(batchGetResultError(result));
      }
    }
  }
}

/**
 * The row to hand the cache: the fresh one, minus the links it would replace
 * with a new signature for bytes the browser already has. A grouped request
 * stays in flight a little longer than a single GET, so an event merged
 * meanwhile can be newer than the answer.
 */
function adopt(
  cached: FileRecord | undefined,
  id: string,
  fresh: FileRecord,
  fingerprint?: string,
): FileRecord {
  if (!cached) {
    noteAnswer(id, fresh, fingerprint);
    return fresh;
  }
  const cachedAt = Date.parse(cached.updated_at);
  if (Number.isFinite(cachedAt) && cachedAt > Date.parse(fresh.updated_at)) {
    return cached;
  }
  const merged = mergeFileRecordLinks(cached, fresh, Date.now());
  noteAnswer(id, merged, fingerprint);
  return merged;
}

/** What the server answered for this row, so the next check can ask short. */
function noteAnswer(id: string, row: FileRecord, fingerprint?: string) {
  const state = rowStates.get(id);
  if (state) {
    state.fingerprint = fingerprint;
    state.updatedAt = row.updated_at;
    state.verifiedAtMs = Date.now();
    return;
  }
  makeRoom(rowStates);
  rowStates.set(id, {
    fingerprint,
    updatedAt: row.updated_at,
    verifiedAtMs: Date.now(),
  });
}

/**
 * Whether the row the cache holds may stand in for an answer the server could
 * not give. A restored row has been stripped of its presigned URLs, and an
 * invalidated one is already known to be behind: resolving either would stamp
 * it settled, and a caller that reads a missing `url` as "this file has no
 * content" would act on a row that never said that. Links about to be renewed
 * are no answer either — they are about to stop working.
 */
function isLiveCachedRow(
  client: QueryClient,
  id: string,
  cached: FileRecord,
): boolean {
  const state = client.getQueryState<FileRecord>(fileRecordKeys.detail(id));
  if (!state || state.isInvalidated) return false;
  if (isRestoredQueryData(state.dataUpdatedAt)) return false;
  const expiresAtMs = earliestPresignExpiryMs(cached);
  if (expiresAtMs === null) return true; // nothing signed to age out
  return expiresAtMs - Date.now() > LINK_HORIZON_MS;
}

/** One GET per id at a time, inside the same in-flight limit as the groups. */
const singleFetches = new Map<string, Promise<FileRecord>>();

function fetchSingle(id: string): Promise<FileRecord> {
  const existing = singleFetches.get(id);
  if (existing) return existing;
  // Same cap as the grouped call, for the same reason: this holds one of the
  // few slots, and a stalled socket would hold it for the life of the tab.
  const request = withBatchGetSlot(() =>
    orgAPI.fileRecords
      .getById(id, { timeout: FILE_RECORD_BATCH_GET_TIMEOUT_MS })
      .catch((error: unknown) => {
        throw isTimeout(error) ? transportError(error) : error;
      }),
  );
  singleFetches.set(id, request);
  void request
    .catch(() => undefined)
    .then(() => {
      if (singleFetches.get(id) === request) singleFetches.delete(id);
    });
  return request;
}

function isTimeout(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { code?: string }).code === "ECONNABORTED"
  );
}

/**
 * The failure of the grouped call itself, as a transient error: never a
 * per-id status, so no caller reads it as a verdict on a row.
 */
function transportError(error: unknown): ApiError {
  const message =
    error instanceof Error ? error.message : "File record batch get failed";
  return new ApiError(message, {
    status: 0,
    statusText: "",
    data: undefined,
    cause: error,
  });
}

/** What `routeTemplate` makes of the grouped call's URL. */
const BATCH_GET_ROUTE = "/api/v2/org/:id/user/:id/batch/file-records";

/**
 * An item's answer is the one the single GET would have given, so it reaches
 * Sentry exactly when that answer would: a handled 4xx stays quiet, a 5xx is
 * a defect. Nothing else sees these — they never pass through the axios
 * interceptor, and an `ApiError` is skipped by the query cache.
 */
function captureBatchGetItemFailure(
  result: FileRecordBatchGetResult | undefined,
) {
  if (!result || result.unsure || result.status === 200) return;
  const context = apiCaptureContext("POST", BATCH_GET_ROUTE, result.status);
  if (!context) return;
  Sentry.captureException(batchGetResultError(result), context);
}

/** The error axios would have produced for the same answer to a single GET. */
function batchGetResultError(
  result: FileRecordBatchGetResult | undefined,
): ApiError {
  if (!result || result.unsure) {
    return new ApiError("File record batch get gave no answer", {
      status: 0,
      statusText: "",
      data: undefined,
    });
  }
  return new ApiError(`Request failed with status code ${result.status}`, {
    status: result.status,
    statusText: "",
    data: { error: { message: result.error?.message } },
  });
}

/** Runs one grouped request inside the per-tab in-flight limit. */
async function withBatchGetSlot<T>(request: () => Promise<T>): Promise<T> {
  await acquireSlot();
  try {
    return await request();
  } finally {
    releaseSlot();
  }
}

function acquireSlot(): Promise<void> {
  if (inFlight < MAX_IN_FLIGHT) {
    inFlight += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    slotQueue.push(() => {
      inFlight += 1;
      resolve();
    });
  });
}

function releaseSlot() {
  inFlight -= 1;
  slotQueue.shift()?.();
}

/**
 * Takes the fresh row, but keeps every cached link that still has more than
 * {@link LINK_HORIZON_MS} left and points at the same bytes: a newly signed
 * address for the same resource would only make the browser download it
 * again. Content that changed takes the fresh links whatever their age.
 */
export function mergeFileRecordLinks(
  cached: FileRecord,
  fresh: FileRecord,
  nowMs: number,
): FileRecord {
  if (cached.content_hash !== fresh.content_hash) return fresh;
  const keep = (oldUrl: string | undefined, newUrl: string | undefined) =>
    !!oldUrl &&
    !!newUrl &&
    sameResource(oldUrl, newUrl) &&
    (getPresignedUrlExpiresAtMs(oldUrl) ?? 0) - nowMs > LINK_HORIZON_MS;

  const merged: FileRecord = { ...fresh };
  let kept = false;
  if (keep(cached.url, fresh.url)) {
    merged.url = cached.url;
    kept = true;
  }
  if (keep(cached.download_url, fresh.download_url)) {
    merged.download_url = cached.download_url;
    kept = true;
  }
  if (fresh.preview_urls && cached.preview_urls) {
    const previews = { ...fresh.preview_urls };
    for (const [size, oldUrl] of Object.entries(cached.preview_urls)) {
      if (keep(oldUrl, previews[size])) {
        previews[size] = oldUrl;
        kept = true;
      }
    }
    merged.preview_urls = previews;
  }
  return kept ? merged : fresh;
}

/** Same address once the CloudFront signature parameters are removed. */
function sameResource(a: string, b: string): boolean {
  const key = presignedResourceKey(a);
  return key !== null && key === presignedResourceKey(b);
}

// ---------------------------------------------------------------------------
// Keeping what is on screen fresh
// ---------------------------------------------------------------------------

interface Checker {
  check: (reason: string, ids?: Set<string>) => Promise<void>;
  stop: () => void;
}

let active: Checker | null = null;
/** Links already renewed once after a load failure, as `${id} ${url}`. */
const renewedAfterFailure = new Set<string>();
/** Runs the checks for this query client until the returned stop is called. */
export function startFileRecordRefresh(queryClient: QueryClient): () => void {
  active?.stop();
  const checker = createChecker(queryClient);
  active = checker;
  return () => {
    if (active === checker) active = null;
    checker.stop();
  };
}

/** The event stream reconnected: events sent while it was down are lost. */
export function notifyFileRecordStreamReconnected() {
  void active?.check("reconnect");
}

function detailId(query: Query): string | null {
  const key = query.queryKey;
  return key.length === 2 &&
    key[0] === "fileRecord" &&
    typeof key[1] === "string"
    ? key[1]
    : null;
}

function createChecker(queryClient: QueryClient): Checker {
  const cache = queryClient.getQueryCache();
  let stopped = false;
  let tickTimer: ReturnType<typeof setTimeout> | null = null;
  /** Ids a check has asked about and not been answered for yet. */
  const asking = new Set<string>();

  /**
   * One pass over the rows something renders: ask about everything not
   * answered for recently, and renew every link inside the horizon. `ids`
   * restricts the pass and renews those rows' links whatever their age.
   */
  const check = async (reason: string, ids?: Set<string>) => {
    if (stopped) return;
    const now = Date.now();
    const items: FileRecordBatchGetItem[] = [];
    for (const query of cache.findAll({ queryKey: ["fileRecord"] })) {
      const id = detailId(query);
      if (!id) continue;
      if (ids && !ids.has(id)) continue;
      if (!ids && asking.has(id)) continue;
      if (query.getObserversCount() === 0) continue;
      const record = (query as Query<FileRecord>).state.data;
      if (!record) continue;
      const expiresAtMs = earliestPresignExpiryMs(record);
      const renew =
        !!ids || (expiresAtMs !== null && expiresAtMs - now <= LINK_HORIZON_MS);
      const state = rowStates.get(id);
      if (!renew && state && now - state.verifiedAtMs < VERIFIED_RECENTLY_MS) {
        continue;
      }
      // Asking with a fingerprint invites `unchanged`, which answers nothing
      // about the links; a row that needs new ones asks in full.
      const fingerprint =
        renew || state?.updatedAt !== record.updated_at
          ? undefined
          : state?.fingerprint;
      items.push(fingerprint ? { id, fingerprint } : { id });
    }
    if (items.length === 0) return;
    debug("check (%s): %d rows", reason, items.length);

    for (const item of items) asking.add(item.id);
    await Promise.all(
      chunked(items).map(async (chunk) => {
        let results: FileRecordBatchGetResult[];
        try {
          results = await withBatchGetSlot(() =>
            orgAPI.fileRecords.batchGet(chunk),
          );
        } catch (error) {
          debug("check (%s) failed: %o", reason, error);
          return;
        } finally {
          for (const item of chunk) asking.delete(item.id);
        }
        if (stopped) return;
        notifyManager.batch(() => {
          for (const result of results) applyResult(queryClient, result);
        });
      }),
    );
  };

  /**
   * Renew one row's links now (a media element that failed on a dead one).
   * Collected over a macrotask like the load window: a canvas whose links all
   * died asks in one call, not one per card.
   */
  let pendingRenewals: Set<string> | null = null;
  const renew = (id: string) => {
    if (pendingRenewals) {
      pendingRenewals.add(id);
      return;
    }
    const renewals = new Set([id]);
    pendingRenewals = renewals;
    setTimeout(() => {
      pendingRenewals = null;
      void check("renew", renewals);
    }, 0);
  };

  const runTick = () => {
    tickTimer = setTimeout(
      () => {
        if (document.visibilityState === "visible") void check("periodic");
        runTick();
      },
      FILE_RECORD_CHECK_INTERVAL_MS + Math.random() * CHECK_JITTER_MS,
    );
  };

  const onVisibility = () => {
    if (document.visibilityState === "visible") void check("visible");
  };
  // Switching between applications usually leaves visibilityState alone,
  // and this is the refetch-on-focus the queries no longer do themselves.
  const onFocus = () => void check("focus");
  const onOnline = () => void check("online");
  const onMediaError = (event: Event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const url =
      target instanceof HTMLImageElement || target instanceof HTMLMediaElement
        ? target.currentSrc || target.src
        : target instanceof HTMLSourceElement
          ? target.src
          : "";
    const id = idForMediaUrl(cache, url);
    if (!id) return;
    // Once per link: a file that can never load must not ask again until the
    // normal schedule comes round.
    const key = `${id} ${url}`;
    if (renewedAfterFailure.has(key)) return;
    makeRoom(renewedAfterFailure);
    renewedAfterFailure.add(key);
    renew(id);
  };

  const stopResumed = onAppResumed(() => void check("app-resumed"));
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("focus", onFocus);
  window.addEventListener("online", onOnline);
  document.addEventListener("error", onMediaError, true);
  runTick();

  return {
    check,
    stop: () => {
      stopped = true;
      stopResumed();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("error", onMediaError, true);
      if (tickTimer) clearTimeout(tickTimer);
    },
  };
}

/** The row a failing media element was rendering. Rare, so a scan is fine. */
function idForMediaUrl(cache: QueryCache, url: string): string | undefined {
  const path = presignedResourceKey(url);
  if (!path) return undefined;
  for (const query of cache.findAll({ queryKey: ["fileRecord"] })) {
    const id = detailId(query);
    if (!id) continue;
    const record = (query as Query<FileRecord>).state.data;
    if (!record) continue;
    for (const link of [
      record.url,
      record.download_url,
      ...Object.values(record.preview_urls ?? {}),
    ]) {
      if (link && presignedResourceKey(link) === path) return id;
    }
  }
  return undefined;
}

function applyResult(
  queryClient: QueryClient,
  result: FileRecordBatchGetResult,
) {
  if (result.unsure) return;
  const key = fileRecordKeys.detail(result.id);
  // The row as it is now, not as it was when the call left: an event or a
  // mutation that landed in flight must not be overwritten, and a row that is
  // gone must not come back.
  const live = queryClient.getQueryData<FileRecord>(key);
  if (!live) return;
  const state = rowStates.get(result.id);
  if (result.status !== 200) {
    // An answer, even a refusal: stamp it, or a canvas holding a dead
    // reference asks twice per trigger — this call, then the invalidation's.
    if (state) state.verifiedAtMs = Date.now();
    // The same path the query takes: refetch through the batcher and land in
    // its error state, so Unavailable and the no-retry rule apply as today.
    void queryClient.invalidateQueries({ queryKey: key });
    return;
  }
  if (result.unchanged) {
    if (state) state.verifiedAtMs = Date.now();
    return;
  }
  const fresh = result.file_record;
  if (!fresh) return;
  const merged = adopt(live, result.id, fresh, result.fingerprint);
  // Nothing moved: writing it would notify every observer for no change.
  if (replaceEqualDeep(live, merged) === live) return;
  queryClient.setQueryData<FileRecord>(key, merged);
}
